/**
 * ProductDetail Page - Mix Design / Recipe summary, read top to bottom:
 *
 *   header  breadcrumb, code + status, description, key facts (grade,
 *           revision, maturity equation), Edit mix design and "..."
 *           (activate / deactivate, delete)
 *   main    Strength vs maturity calibration (active curve + top 3),
 *           Strength targets, Temperature profile. Each section card has
 *           its title, a one-line purpose and its actions in the header.
 *   side    Monitoring settings and the record
 *           (created / last changed)
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Breadcrumbs,
  Typography,
  Button,
  Grid,
  Alert,
  Snackbar,
  CircularProgress,
  Chip,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Link,
  Menu,
  MenuItem,
  Tooltip,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  MoreHoriz as MoreIcon,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import productsService, {
  Product,
  type SupplierPlant,
  type ProductPlantItem,
  type MassConcreteDefault,
  type EvaporationDefault,
  type TargetGroup,
} from '../services/products';
import ProductTempProfileCard from '../components/ProductTempProfileCard';
import ProductTargetGroupsTab from '../components/ProductTargetGroupsTab';
import ProductCalibrationsTab from '../components/ProductCalibrationsTab';
import { useAuth } from '../context/AuthContext';
import { hasPermission } from '../utils/userPermissions';
import { useCompanyStrengthUnit } from '../utils/useCompanyStrengthUnit';
import { maturityConstantsDetail, maturityEquationName } from '../utils/maturityEquation';
import { apiErrorText } from '../utils/apiError';
import SectionCard, { PropertyRow } from '../components/product/SectionCard';

/** Audit timestamps are stored as naive UTC — parse them as UTC and render
 *  in the browser's local time (see timezone convention in datetime.ts). */
const formatDateTime = (iso?: string | null): string => {
  if (!iso) return '—';
  const hasZone = /Z|[+-]\d{2}:?\d{2}$/.test(iso);
  const d = new Date(hasZone ? iso : `${iso}Z`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

/** A key fact in the page header: small label over the value. */
const HeaderFact: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography variant="caption" color="text.secondary" component="div" sx={{ fontWeight: 500 }}>
      {label}
    </Typography>
    <Typography variant="body2" fontWeight={600} component="div" noWrap>
      {value}
    </Typography>
  </Box>
);

const ProductDetail: React.FC = () => {
  const { t } = useTranslation();
  const { productId } = useParams<{ productId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canManage = hasPermission(user, 'mix_designs.edit');
  const canDelete = hasPermission(user, 'mix_designs.deactivate');
  const canLinkPlants = hasPermission(user, 'mix_designs.link_batch_plants') || canManage;
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [plantsOpen, setPlantsOpen] = useState(false);

  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Lookups for resolving the default group names in the Details panel
  const [massConcreteDefaults, setMassConcreteDefaults] = useState<MassConcreteDefault[]>([]);
  const [evaporationDefaults, setEvaporationDefaults] = useState<EvaporationDefault[]>([]);
  const [targetGroups, setTargetGroups] = useState<TargetGroup[]>([]);

  // Grade strength is in the mix design company's unit (StrengthFormat);
  // shown without a unit until that is known.
  const strengthUnit = useCompanyStrengthUnit(product?.company_id);

  // Plant linking
  const [allPlants, setAllPlants] = useState<SupplierPlant[]>([]);
  const [plantLinks, setPlantLinks] = useState<ProductPlantItem[]>([]);
  const [togglingPlantId, setTogglingPlantId] = useState<string | null>(null);

  const loadProduct = useCallback(async () => {
    if (!productId) return;

    setLoading(true);
    setError(null);

    try {
      const [data, plants, links, mcDefaults, evapDefaults, groups] = await Promise.all([
        productsService.getProduct(productId),
        productsService.listSupplierPlants(undefined, true).catch(() => [] as SupplierPlant[]),
        productsService.getProductPlants(productId).catch(() => [] as ProductPlantItem[]),
        productsService.listMassConcreteDefaults().catch(() => [] as MassConcreteDefault[]),
        productsService.listEvaporationDefaults().catch(() => [] as EvaporationDefault[]),
        productsService.listTargetGroups(productId).catch(() => [] as TargetGroup[]),
      ]);
      setProduct(data);
      setAllPlants(plants);
      setPlantLinks(links);
      setMassConcreteDefaults(mcDefaults);
      setEvaporationDefaults(evapDefaults);
      setTargetGroups(groups);
    } catch (err: any) {
      setError(apiErrorText(err, t('products.failedToLoad')));
    } finally {
      setLoading(false);
    }
  }, [productId]);

  useEffect(() => {
    if (productId) {
      loadProduct();
    }
  }, [productId, loadProduct]);

  const linkedIds = useMemo(
    () => new Set(plantLinks.map((l) => l.supplier_plant_id)),
    [plantLinks]
  );

  const handleTogglePlant = async (plant: SupplierPlant) => {
    if (!productId) return;
    const isLinked = linkedIds.has(plant.supplier_plant_id);
    setTogglingPlantId(plant.supplier_plant_id);
    try {
      if (isLinked) {
        await productsService.unlinkSupplierPlantProduct(plant.supplier_plant_id, productId);
        setPlantLinks((prev) => prev.filter((l) => l.supplier_plant_id !== plant.supplier_plant_id));
        setSuccess(t('productDetail.plantUnlinked', 'Batching plant unlinked'));
      } else {
        const item = await productsService.linkSupplierPlantProduct(plant.supplier_plant_id, productId);
        setPlantLinks((prev) => [
          ...prev,
          {
            supplier_plant_id: plant.supplier_plant_id,
            name: plant.name,
            ready_mix_supplier: plant.ready_mix_supplier ?? null,
            date_linked: item.date_linked ?? null,
          },
        ]);
        setSuccess(t('productDetail.plantLinked', 'Batching plant linked'));
      }
    } catch (err: any) {
      setError(apiErrorText(err, t('productDetail.plantToggleFailed', 'Failed to update plant link')));
    } finally {
      setTogglingPlantId(null);
    }
  };

  const handleBack = () => {
    navigate('/products');
  };

  const handleEdit = () => {
    navigate(`/products/${productId}/edit`);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!product) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{t('products.productNotFound')}</Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={handleBack} sx={{ mt: 2 }}>
          {t('products.backToProducts')}
        </Button>
      </Box>
    );
  }

  const active = product.is_active !== false;
  const tempGroupName = massConcreteDefaults.find(
    (d) => d.id === product.mass_concrete_default_id
  )?.description;
  const defaultTargetsName = targetGroups.find(
    (g) => g.target_group_id === product.target_group_id
  )?.target_group_name;
  const evapGroupName = evaporationDefaults.find(
    (d) => d.id === product.evaporation_default_id
  )?.description;

  const notSet = (text = t('productDetail.notSet', 'Not set')) => (
    <Typography component="span" variant="body2" color="text.disabled">{text}</Typography>
  );
  const grade = product.strength_grade != null
    ? `${product.strength_grade}${strengthUnit ? ` ${strengthUnit}` : ''}`
    : null;
  const created = product.date_create
    ? `${formatDateTime(product.date_create)}${product.create_by ? ` by ${product.create_by}` : ''}`
    : null;
  const changed = product.date_last_modified
    ? `${formatDateTime(product.date_last_modified)}${product.modified_by ? ` by ${product.modified_by}` : ''}`
    : null;

  const handleToggleActive = async () => {
    setMenuAnchor(null);
    try {
      await productsService.updateProduct(product.product_id, { is_active: !active });
      setSuccess(active ? t('products.deactivated', 'Mix design deactivated') : t('products.activated', 'Mix design activated'));
      loadProduct();
    } catch (err: any) {
      setError(apiErrorText(err, t('products.toggleFailed', 'Failed to update status')));
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await productsService.deleteProduct(product.product_id);
      navigate('/products');
    } catch (err: any) {
      setError(apiErrorText(err, t('products.failedToDelete', 'Failed to delete')));
      setDeleteOpen(false);
    } finally {
      setDeleting(false);
    }
  };

  const equationName = maturityEquationName(product);
  const equationDetail = maturityConstantsDetail(product);

  return (
    <Box sx={{ maxWidth: 1440, mx: 'auto' }}>
      {/* Page header: where you are, what the mix is, what you can do */}
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 1, fontSize: '0.8125rem' }}>
        <Link
          component="button"
          underline="hover"
          color="text.secondary"
          onClick={handleBack}
          sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontSize: 'inherit' }}
        >
          <ArrowBackIcon sx={{ fontSize: 16 }} />
          {t('productDetail.breadcrumbRoot', 'Mix designs')}
        </Link>
        <Typography color="text.primary" sx={{ fontSize: 'inherit' }}>{product.code}</Typography>
      </Breadcrumbs>

      <Box
        component="header"
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'stretch', md: 'flex-start' },
          gap: 2,
          mb: 3,
        }}
      >
        <Box sx={{ minWidth: 0, flexGrow: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <Typography variant="h5" component="h2" sx={{ lineHeight: 1.2 }}>{product.code}</Typography>
            <Chip
              size="small"
              label={active ? t('products.statusActive', 'Active') : t('products.statusInactive', 'De-Active')}
              color={active ? 'success' : 'default'}
            />
          </Box>
          {product.description && (
            <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5 }}>{product.description}</Typography>
          )}
          <Box sx={{ display: 'flex', gap: { xs: 3, sm: 5 }, mt: 2, flexWrap: 'wrap' }} data-testid="header-facts">
            <HeaderFact label={t('productDetail.strengthGrade', 'Strength grade')} value={grade || '—'} />
            <HeaderFact label={t('productDetail.revision', 'Revision')} value={product.rev || '—'} />
            <HeaderFact
              label={t('productDetail.maturityEquation', 'Maturity equation')}
              value={equationName || t('productDetail.systemDefault', 'System default')}
            />
          </Box>
        </Box>
        {(canManage || canDelete) && (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexShrink: 0 }}>
            {canManage && (
              <Button variant="contained" startIcon={<EditIcon />} onClick={handleEdit}>
                {t('productDetail.editMix', 'Edit mix design')}
              </Button>
            )}
            <Tooltip title={t('productDetail.moreActions', 'More actions')}>
              <IconButton
                aria-label="more actions"
                onClick={(e) => setMenuAnchor(e.currentTarget)}
                sx={{ border: 1, borderColor: 'divider', borderRadius: 1.5 }}
              >
                <MoreIcon />
              </IconButton>
            </Tooltip>
            <Menu
              anchorEl={menuAnchor}
              open={!!menuAnchor}
              onClose={() => setMenuAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              {canManage && (
                <MenuItem onClick={handleToggleActive}>
                  {active ? t('productDetail.deactivate', 'Deactivate') : t('productDetail.activate', 'Activate')}
                </MenuItem>
              )}
              {canDelete && (
                <MenuItem onClick={() => { setMenuAnchor(null); setDeleteOpen(true); }} sx={{ color: 'error.main' }}>
                  {t('common.delete', 'Delete')}
                </MenuItem>
              )}
            </Menu>
          </Box>
        )}
      </Box>

      {!active && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          {t('productDetail.inactiveBanner', 'This mix design is deactivated and hidden from active lists and the pour wizard.')}
        </Alert>
      )}

      <Grid container spacing={3} alignItems="flex-start">
        {/* Main column: what pours of this mix use */}
        <Grid size={{ xs: 12, lg: 8 }} sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <SectionCard
            title={t('productDetail.calibrationsTitle', 'Strength vs maturity calibration')}
            subtitle={t('productDetail.calibrationsSubtitle', 'The active curve turns maturity into an estimated strength for every pour of this mix.')}
          >
            <ProductCalibrationsTab
              compact
              productId={productId!}
              onError={(msg) => setError(msg)}
              onSuccess={(msg) => setSuccess(msg)}
            />
          </SectionCard>

          <SectionCard
            title={t('productDetail.tabTargetGroups', 'Strength targets')}
            subtitle={t('productDetail.targetsSubtitle', 'Strengths pour setup tracks, such as stripping or stressing. Open a group to see its targets.')}
          >
            <ProductTargetGroupsTab
              productId={productId!}
              defaultGroupId={product.target_group_id ?? null}
              maturityMethod={product.maturity_method ?? null}
              specifiedTemp={product.maturity_specified_temp ?? null}
              strengthUnit={strengthUnit}
              onError={(msg) => setError(msg)}
              onSuccess={(msg) => setSuccess(msg)}
            />
          </SectionCard>

          <SectionCard
            title={t('productDetail.temperatureProfile', 'Temperature profile')}
            subtitle={t('productDetail.tempProfileSubtitle', 'How much this mix heats up after placing: each line is one pour’s temperature rise over its first 7 days.')}
          >
            <ProductTempProfileCard
              productId={productId!}
              canManage={canManage}
              onError={(msg) => setError(msg)}
              onSuccess={(msg) => setSuccess(msg)}
            />
          </SectionCard>
        </Grid>

        {/* Side column: settings applied when the mix is monitored, and the record */}
        <Grid size={{ xs: 12, lg: 4 }}>
          <SectionCard
            title={t('productDetail.settingsTitle', 'Monitoring settings')}
            subtitle={t('productDetail.settingsSubtitle', 'Applied to new pours of this mix.')}
            action={canManage ? (
              <Button size="small" variant="outlined" onClick={handleEdit}>{t('common.edit', 'Edit')}</Button>
            ) : undefined}
          >
            <Box component="dl" sx={{ m: 0, mt: -1.25, mb: -1.25 }}>
              <PropertyRow
                label={t('productDetail.maturityEquation', 'Maturity equation')}
                value={
                  equationName ? (
                    <>
                      {equationName}
                      {equationDetail && (
                        <Typography component="span" variant="body2" color="text.secondary" display="block">
                          {equationDetail}
                        </Typography>
                      )}
                    </>
                  ) : (
                    <Typography component="span" variant="body2" color="warning.main" data-testid="no-maturity-equation">
                      {t('productDetail.noMaturityEquation', 'Not set: pours use the system default')}
                    </Typography>
                  )
                }
              />
              <PropertyRow label={t('productDetail.tempGroup', 'Temperature limits')} value={tempGroupName || notSet()} />
              <PropertyRow label={t('productDetail.evapGroup', 'Evaporation limits')} value={evapGroupName || notSet()} />
              <PropertyRow label={t('productDetail.strengthGroup', 'Default strength targets')} value={defaultTargetsName || notSet()} />
              <PropertyRow
                last
                label={t('productDetail.linkedPlants', 'Batching plants')}
                value={
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
                    <Box>
                      {plantLinks.length > 0
                        ? plantLinks.map((l) => <div key={l.supplier_plant_id}>{l.name}</div>)
                        : notSet(t('productDetail.noLinkedPlants', 'None linked'))}
                    </Box>
                    {canLinkPlants && allPlants.length > 0 && (
                      <Link component="button" variant="body2" onClick={() => setPlantsOpen(true)} sx={{ flexShrink: 0 }}>
                        {t('productDetail.changePlants', 'Change')}
                      </Link>
                    )}
                  </Box>
                }
              />
            </Box>
          </SectionCard>

          <Box sx={{ px: 1, pt: 2 }} data-testid="record-info">
            <Typography variant="caption" color="text.secondary" component="p">
              {t('productDetail.created', 'Created')} {created || '—'}
            </Typography>
            <Typography variant="caption" color="text.secondary" component="p">
              {changed
                ? `${t('productDetail.lastChangedLabel', 'Last changed')} ${changed}`
                : t('productDetail.notChangedYet', 'Not changed since it was created')}
            </Typography>
          </Box>
        </Grid>
      </Grid>

      {/* Delete confirmation */}
      <Dialog open={deleteOpen} onClose={() => !deleting && setDeleteOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('productDetail.deleteTitle', 'Delete mix design?')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            <strong>{product.code}</strong>{' '}
            {t('productDetail.deleteBody', 'is deleted for good. A mix design that pours have used cannot be deleted; deactivate it instead.')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)} disabled={deleting}>{t('common.cancel', 'Cancel')}</Button>
          <Button color="error" variant="contained" onClick={handleDelete} disabled={deleting}>
            {t('common.delete', 'Delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Batching plants: pick which plants supply this mix */}
      <Dialog open={plantsOpen} onClose={() => setPlantsOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('productDetail.plantsDialogTitle', 'Batching plants for this mix')}</DialogTitle>
        <DialogContent dividers>
          {allPlants.map((plant) => (
            <FormControlLabel
              key={plant.supplier_plant_id}
              sx={{ display: 'flex' }}
              control={
                <Checkbox
                  size="small"
                  checked={linkedIds.has(plant.supplier_plant_id)}
                  disabled={togglingPlantId === plant.supplier_plant_id}
                  onChange={() => handleTogglePlant(plant)}
                />
              }
              label={
                <span>
                  {plant.name}
                  {plant.ready_mix_supplier && (
                    <Typography component="span" variant="body2" color="text.secondary">{` · ${plant.ready_mix_supplier}`}</Typography>
                  )}
                </span>
              }
            />
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPlantsOpen(false)}>{t('common.done', 'Done')}</Button>
        </DialogActions>
      </Dialog>

      {/* Success Snackbar */}
      <Snackbar open={!!success} autoHideDuration={6000} onClose={() => setSuccess(null)}>
        <Alert severity="success" onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      </Snackbar>

      {/* Error Snackbar */}
      <Snackbar open={!!error} autoHideDuration={6000} onClose={() => setError(null)}>
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ProductDetail;
